import type { SyntheticEvent } from "react";
import { useCallback, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";

import CircularProgress from "components/circular-progress";
import { useLazyGetStaffTaskCompletionAuditQuery } from "services/staff-tasks";
import type { StaffTask } from "services/types/staff-tasks.type";
import { formatDateToDateAndTime } from "utils/common";

function formatCompletionDate(date: string | null | undefined) {
  if (!date) {
    return "Unavailable";
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return "Unavailable";
  }

  return formatDateToDateAndTime(parsedDate);
}

type StaffTaskCompletionDetailsProps = {
  task: StaffTask;
};

export default function StaffTaskCompletionDetails({
  task
}: StaffTaskCompletionDetailsProps) {
  const [expanded, setExpanded] = useState(false);
  const [hasRequested, setHasRequested] = useState(false);
  const [loadCompletionAudit, { currentData, isFetching, isError }] =
    useLazyGetStaffTaskCompletionAuditQuery();

  useEffect(() => {
    setExpanded(false);
    setHasRequested(false);
  }, [task.id]);

  const handleChange = useCallback(
    (_event: SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded);
      if (isExpanded && !hasRequested) {
        setHasRequested(true);
        loadCompletionAudit(task.id);
      }
    },
    [hasRequested, loadCompletionAudit, task.id]
  );

  const completedByName =
    currentData?.completed_by?.fullName ||
    currentData?.completed_by?.full_name ||
    currentData?.completed_by?.name ||
    "Unavailable";
  const completedAt = formatCompletionDate(currentData?.completed_at);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={handleChange}
      sx={{
        border: `1px solid ${grey[200]}`,
        borderRadius: "8px",
        "&:before": { display: "none" }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: "40px", px: 2 }}
      >
        <Typography variant="body2" fontWeight={600}>
          Completion Details
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        {isFetching && <CircularProgress sx={{ width: "100%", py: 1 }} />}
        {!isFetching && isError && (
          <Typography variant="body2" color="error">
            Unable to load completion details.
          </Typography>
        )}
        {!isFetching && !isError && currentData && !currentData.completed_at && (
          <Typography variant="body2" color={grey[700]}>
            This task has not been completed.
          </Typography>
        )}
        {!isFetching && !isError && currentData?.completed_at && (
          <Stack rowGap={1}>
            <Box>
              <Typography variant="caption" color={grey[600]}>
                Completed By
              </Typography>
              <Typography variant="body2" color={grey[900]}>
                {completedByName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color={grey[600]}>
                Completed At
              </Typography>
              <Typography variant="body2" color={grey[900]}>
                {completedAt}
              </Typography>
            </Box>
            {!currentData.is_reliable && (
              <Typography variant="caption" color={grey[600]}>
                Completing user is unavailable for this task.
              </Typography>
            )}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
